import React from 'react';
import {
  Box,
  Controller,
  ControllerWrapper,
  FieldTypes,
  FieldWrapper,
  FlexRadioGroup,
  FormFieldsContainer,
  Hint,
  Label,
  RegularField,
  SelectDropdown,
  Text,
  useFormContext,
} from '@common-fe/common-fe';

import { DEFAULT_EMPTY_CHARACTER } from '@/common/constants';
import { NO_KEY, YES_KEY } from '@/common/constants';
import { Option } from '@/common/types';
import {
  IS_LIMITED_OPTIONS,
  PayoutDefinitionField,
  PayoutDefinitionFormValues,
} from '@/modules/ProcessingDefinition/PayoutDefinition/PayoutDefinitionSetup/PayoutDefinitionSetup.types';

interface BasicPropertiesProps {
  isEditMode: boolean;
  canBeLimited: boolean;
  mpTableOptions: Option[];
  covTableOptions: Option[];
}

const BasicProperties: React.FC<BasicPropertiesProps> = ({
  isEditMode,
  canBeLimited,
  mpTableOptions,
  covTableOptions,
}) => {
  const {
    control,
    formState: { errors },
  } = useFormContext<PayoutDefinitionFormValues>();

  const getMpTableName = (value: string) => mpTableOptions.find((opt) => opt.value === value)?.title;

  const getCovTableName = (value: string) => covTableOptions.find((opt) => opt.value === value)?.title;

  return (
    <Box
      data-testid="basic-properties_wrapper"
      background="canvas"
      border={{ color: 'border2', size: 'small' }}
      round="container1Round"
      pad={{ vertical: 'spacing24', horizontal: 'spacing32' }}
    >
      <FormFieldsContainer>
        <FieldWrapper>
          <Label width="320px" showRequireIcon name={PayoutDefinitionField.payoutDefinitionName} isNarrow>
            Payout definition name
          </Label>
          <ControllerWrapper style={{ flex: 'none', width: '320px' }}>
            <Controller
              name={PayoutDefinitionField.payoutDefinitionName}
              control={control}
              render={({ field }) => {
                if (!isEditMode) {
                  return (
                    <Text size="medium" weight="normal" data-testid="basic-properties_name-text">
                      {field.value || DEFAULT_EMPTY_CHARACTER}
                    </Text>
                  );
                }
                return (
                  <RegularField
                    testId="basic-properties_name-input"
                    name={PayoutDefinitionField.payoutDefinitionName}
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Enter payout definition name"
                    isError={!!errors[PayoutDefinitionField.payoutDefinitionName]?.message}
                    errorText={errors[PayoutDefinitionField.payoutDefinitionName]?.message}
                    editMode
                  />
                );
              }}
            />
          </ControllerWrapper>
        </FieldWrapper>

        <FieldWrapper>
          <Label width="320px" showRequireIcon name={PayoutDefinitionField.mpTableId} isNarrow>
            MP identifier
          </Label>
          <ControllerWrapper style={{ flex: 'none', width: '320px' }}>
            <Controller
              name={PayoutDefinitionField.mpTableId}
              control={control}
              render={({ field }) => {
                if (!isEditMode) {
                  return (
                    <Text size="medium" weight="normal" data-testid="basic-properties_mp-table-text">
                      {getMpTableName(field.value) || DEFAULT_EMPTY_CHARACTER}
                    </Text>
                  );
                }
                return (
                  <SelectDropdown
                    testId="basic-properties_mp-table-dropdown"
                    options={mpTableOptions}
                    value={field.value}
                    onChange={(value) => field.onChange(value)}
                    placeholder="Select MP identifier"
                    singleMode
                    id={PayoutDefinitionField.mpTableId}
                    name={PayoutDefinitionField.mpTableId}
                    errorText={errors[PayoutDefinitionField.mpTableId]?.message}
                  />
                );
              }}
            />
          </ControllerWrapper>
        </FieldWrapper>

        <FieldWrapper>
          <Label width="320px" showRequireIcon name={PayoutDefinitionField.covTableId} isNarrow>
            COV table
          </Label>
          <ControllerWrapper style={{ flex: 'none', width: '320px' }}>
            <Controller
              name={PayoutDefinitionField.covTableId}
              control={control}
              render={({ field }) => {
                if (!isEditMode) {
                  return (
                    <Text size="medium" weight="normal" data-testid="basic-properties_cov-table-text">
                      {getCovTableName(field.value) || 'Default'}
                    </Text>
                  );
                }
                return (
                  <SelectDropdown
                    testId="basic-properties_cov-table-dropdown"
                    options={covTableOptions}
                    value={field.value}
                    onChange={(value) => field.onChange(value)}
                    placeholder="Select COV table"
                    singleMode
                    id={PayoutDefinitionField.covTableId}
                    name={PayoutDefinitionField.covTableId}
                    errorText={errors[PayoutDefinitionField.covTableId]?.message}
                  />
                );
              }}
            />
          </ControllerWrapper>
        </FieldWrapper>

        <FieldWrapper>
          <Label width="320px" name={PayoutDefinitionField.isLimited} isNarrow>
            Is limited?
          </Label>
          <ControllerWrapper style={{ flex: 'none', width: 'auto' }}>
            <Controller
              name={PayoutDefinitionField.isLimited}
              control={control}
              render={({ field }) => {
                const isDisabled = !isEditMode || !canBeLimited;

                const radioGroup = (
                  <FlexRadioGroup
                    type={FieldTypes.Radio}
                    name={PayoutDefinitionField.isLimited}
                    value={field.value ? YES_KEY : NO_KEY}
                    onChange={(value) => field.onChange(value === YES_KEY)}
                    options={IS_LIMITED_OPTIONS}
                    disabled={isDisabled}
                  />
                );

                if (!isEditMode || canBeLimited) {
                  return radioGroup;
                }

                return (
                  <Hint hideHint={false} hintAlign="right" hintWidth={220} hintElement={radioGroup}>
                    <Text
                      color="textOnColor"
                      textAlign="center"
                      data-testid="basic-properties_is-limited-hint"
                    >
                      Limited payout definition has already been set up for your organization
                    </Text>
                  </Hint>
                );
              }}
            />
          </ControllerWrapper>
        </FieldWrapper>
      </FormFieldsContainer>
    </Box>
  );
};

export default BasicProperties;
